/**
 * TOSS Web Application - Toast Component
 *
 * Toast notification system for displaying temporary messages.
 * Integrates with the UI store for state management.
 *
 * Вид (решение владельца 27.08.2026): «тихая карточка». Плашка НЕЙТРАЛЬНАЯ — поверхность
 * карточки, обычная рамка, радиус 12; цвет статуса живёт единственной точкой — кружком иконки.
 * Прежняя пастельная заливка всей плашки и цветная полоса прогресса — то самое «цветовое
 * кодирование блоками», которое владелец забраковал по всему продукту.
 *
 * Features:
 * - Toast stacking: Max 3 visible toasts, newest FIRST in the DOM (they sit at the top edge)
 * - Progress bar: Thin neutral bar at bottom showing remaining time before auto-dismiss
 * - Slide-in/slide-out animation: Smooth entry and exit transitions
 * - Auto-dismiss timer lives HERE, not in the store: only this component knows whether the
 *   cursor is over the toast, and a toast under the cursor must not disappear mid-sentence.
 *
 * @example
 * ```tsx
 * // In App.tsx
 * <ToastContainer />
 *
 * // Using toasts
 * const { showToast } = useToast();
 * showToast({ type: 'success', message: 'Operation completed!' });
 * ```
 */

import { useEffect, useCallback, useState, useRef, memo } from 'react';
import { createPortal } from 'react-dom';
import { Check, TriangleAlert, Info, X, type LucideIcon } from 'lucide-react';
import { useUIStore, selectToasts, type Toast } from '@/store/uiStore';

// ============================================================================
// Constants
// ============================================================================

/** Maximum number of visible toasts at once */
const MAX_VISIBLE_TOASTS = 3;

/** Duration of the slide-out animation in ms */
const SLIDE_OUT_DURATION = 200;

// ============================================================================
// Types
// ============================================================================

/**
 * Toast type variants
 */
export type ToastType = 'success' | 'error' | 'warning' | 'info';

/**
 * Single toast item props
 */
interface ToastItemProps {
  toast: Toast;
  onDismiss: (id: string) => void;
}

/** Как тип тоста опознаётся глазом: глиф в цветном кружке — и больше нигде */
interface ToastVisual {
  Icon: LucideIcon;
  /** Подложка кружка — subdued-оттенок статуса */
  circle: string;
  /** Глиф внутри кружка — `*-text`-токен, заведённый под контраст на subdued-фоне */
  glyph: string;
}

// ============================================================================
// Toast visuals
// ============================================================================

/* `X` у ошибки и `X` кнопки закрытия — намеренно разные по весу: у ошибки он 12 px внутри
   красного кружка, у кнопки — 16 px без подложки и серый. Подложка и есть различитель. */
const toastVisualByType: Record<ToastType, ToastVisual> = {
  success: {
    Icon: Check,
    circle: 'bg-[var(--color-success-subdued)]',
    glyph: 'text-[var(--color-success-text)]',
  },
  error: {
    Icon: X,
    circle: 'bg-[var(--color-error-subdued)]',
    glyph: 'text-[var(--color-error-text)]',
  },
  warning: {
    Icon: TriangleAlert,
    circle: 'bg-[var(--color-warning-subdued)]',
    glyph: 'text-[var(--color-warning-text)]',
  },
  info: {
    Icon: Info,
    circle: 'bg-[var(--color-info-subdued)]',
    glyph: 'text-[var(--color-info-text)]',
  },
};

// ============================================================================
// Toast Item Component
// ============================================================================

/**
 * Individual toast notification item with progress bar and slide-out animation
 */
const ToastItem = memo(function ToastItem({
  toast,
  onDismiss,
}: ToastItemProps): JSX.Element {
  const { id, type, title, message, duration } = toast;
  const [isExiting, setIsExiting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const remainingRef = useRef(duration && duration > 0 ? duration : 0);
  const startTimeRef = useRef(Date.now());

  const hasDuration = duration != null && duration > 0;
  /** Две строки или одна: от этого зависит, равнять ряд по центру или по первой строке */
  const hasDetails = Boolean(title && message);

  // Dismiss with slide-out animation
  const handleDismiss = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      onDismiss(id);
    }, SLIDE_OUT_DURATION);
  }, [id, onDismiss]);

  // Auto-dismiss timer with pause/resume support
  useEffect(() => {
    if (!hasDuration) return;

    if (isPaused) {
      // Clear any existing timer on pause
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      // Calculate remaining time
      remainingRef.current = remainingRef.current - (Date.now() - startTimeRef.current);
      if (remainingRef.current <= 0) remainingRef.current = 0;
      return;
    }

    // Start/resume timer
    startTimeRef.current = Date.now();
    timerRef.current = setTimeout(() => {
      handleDismiss();
    }, remainingRef.current);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [hasDuration, isPaused, handleDismiss]);

  const { Icon, circle, glyph } = toastVisualByType[type] ?? toastVisualByType.info;

  const animationClass = isExiting
    ? 'animate-[toast-slide-out_200ms_ease-in_forwards]'
    : 'animate-[toast-slide-in_200ms_ease-out]';

  /* Поверхность и рамка берутся токенами, а не белым хардкодом: при выбранном фоне приложения
     (`:root[data-app-bg="custom"]`) оба пересчитываются через color-mix, и тост остаётся в тон
     карточкам под ним. Радиус — `--radius-lg` явной переменной: Tailwind-класс `rounded-lg`
     подставил бы свои 8 px, потому что `--radius-*` объявлены в `:root`, а не в `@theme`. */
  return (
    <div
      className={`pointer-events-auto relative w-full max-w-sm overflow-hidden app-surface app-surface-outlined shadow-[var(--shadow-overlay)] ${animationClass} ${toast.onClick ? 'cursor-pointer' : ''}`}
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onClick={() => {
        if (toast.onClick) {
          toast.onClick();
          handleDismiss();
        }
      }}
    >
      <div className={`flex ${hasDetails ? 'items-start' : 'items-center'} gap-2.5 p-3`}>
        {/* Icon — единственное цветное пятно карточки */}
        <span
          className={`flex h-5 w-5 flex-none items-center justify-center rounded-full ${circle}`}
        >
          <Icon className={`h-3 w-3 ${glyph}`} aria-hidden={true} />
        </span>

        {/* Content */}
        <div className="min-w-0 flex-1">
          {hasDetails ? (
            <>
              <p className="text-md font-medium text-[var(--color-text-primary)]">{title}</p>
              <p className="mt-0.5 text-sm text-[var(--color-text-secondary)] line-clamp-2">{message}</p>
            </>
          ) : (
            <p className="text-md font-medium text-[var(--color-text-primary)]">{message || title}</p>
          )}
        </div>

        {/* Dismiss button. `-mr-0.5` — оптика, а не небрежность: крестик 16 px живёт в боксе
            20 px, и при равном отступе до бокса его глиф отходит от края на 2 px дальше, чем
            заливка кружка слева. Сдвиг возвращает обеим сторонам один визуальный отступ. */}
        <button
          type="button"
          className="touch-area-expanded -mr-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-[var(--radius-md)] text-[var(--color-text-disabled)] hover:text-[var(--color-text-secondary)] focus:outline-none focus:ring-2 focus:ring-primary-500"
          onClick={handleDismiss}
        >
          <span className="sr-only">Закрыть</span>
          <X className="h-4 w-4" aria-hidden={true} />
        </button>
      </div>

      {/* Progress bar — нейтральный: он сообщает время, а не статус. Absolute, а не в потоке:
          в потоке его 2 px съедали нижний отступ, и содержимое вставало выше центра карточки. */}
      {hasDuration && (
        <div className="absolute inset-x-0 bottom-0 h-0.5">
          <div
            className="h-full bg-[var(--color-text-disabled)]"
            style={{
              animation: `toast-progress ${duration}ms linear forwards`,
              animationPlayState: isPaused ? 'paused' : 'running',
            }}
          />
        </div>
      )}
    </div>
  );
});

// ============================================================================
// Toast Container Component
// ============================================================================

/**
 * Toast container component
 *
 * Renders active toasts in a fixed position container.
 * Max 3 visible toasts at once, newest first.
 * Should be placed once at the root of the application.
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <>
 *       <AppContent />
 *       <ToastContainer />
 *     </>
 *   );
 * }
 * ```
 */
export function ToastContainer(): JSX.Element | null {
  const toasts = useUIStore(selectToasts);
  const dismissToast = useUIStore((state) => state.removeToast);

  const handleDismiss = useCallback(
    (id: string) => {
      dismissToast(id);
    },
    [dismissToast]
  );

  // Don't render anything if no toasts
  if (toasts.length === 0) {
    return null;
  }

  /* Показываем последние MAX_VISIBLE_TOASTS и разворачиваем: стопка растёт от ВЕРХНЕЙ кромки
     экрана, поэтому свежий тост должен быть первым в DOM — иначе новое сообщение появляется
     под старыми и уезжает от глаза. */
  const visibleToasts = toasts.slice(-MAX_VISIBLE_TOASTS).reverse();

  // Container uses z-[var(--z-toast)] (1080) so toasts sit ABOVE modals
  // (--z-modal 1050) and side panels. With a lower z-index, a toast fired from
  // inside a modal — including the global mutation-error toast from queryClient —
  // renders behind the modal (fullscreen & opaque on mobile) and is invisible.
  return createPortal(
    <div
      aria-live="assertive"
      className="pointer-events-none fixed inset-0 z-[var(--z-toast)] flex flex-col items-end gap-2 p-4 pt-[calc(1rem+env(safe-area-inset-top,0px))] sm:items-start sm:p-6 sm:pt-[calc(1.5rem+env(safe-area-inset-top,0px))]"
    >
      {/* Toast list positioned at top-right on desktop, top on mobile */}
      <div className="flex w-full flex-col items-center gap-2 sm:items-end">
        {visibleToasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={handleDismiss} />
        ))}
      </div>
    </div>,
    document.body
  );
}

// ============================================================================
// Default Export
// ============================================================================

export default ToastContainer;
